<template>
  <div
    class="agenda-day-con"
    :id="`agenda-day-${conferenceDay.conference_days_id}`"
    data-cy="teacher-agenda-card"
  >
    <div class="day-top">
      <div>
        <Typography class="day-index" color="gray600" weight="600" size="1.25rem">
          DAY {{ props.dayIndex + 1 }}
        </Typography>
        <Typography style="vertical-align: bottom" color="gray600" weight="600">
          {{ dayjs(props.conferenceDay.start).format("dddd, MMMM D, YYYY") }}
        </Typography>
      </div>
      <div class="d-flex align-center gap-3">
        <typography
          color="gray600"
          weight="600"
          transform="uppercase"
          v-if="
            (props.conferenceDay.room || authStore.me.room_assignment) &&
            $vuetify.display.mdAndUp &&
            conferenceDay.teacher_meeting_types?.in_person &&
            conferenceDay.meeting_types?.in_person
          "
        >
          Room: {{ props.conferenceDay.room || authStore.me.room_assignment }}
        </typography>
        <a
          v-if="
            props.conferenceDay.virtual_room &&
            props.conferenceDay.meeting_types?.virtual &&
            props.conferenceDay.teacher_meeting_types?.virtual
          "
          :href="
          (props.conferenceDay.virtual_room.match(/^https?:\/\//) ? '' : 'https://') + props.conferenceDay.virtual_room

          "
          target="_blank"
          
          rel="noopener noreferrer"
        >
          <v-btn>
            <template #prepend>
              <v-icon color="primary300">mdi-monitor-account</v-icon>
            </template>
            start meeting</v-btn
          >
        </a>
        <v-btn
          v-if="
            conferenceDay.meeting_types &&
            (conferenceDay.meeting_types.callback ||
              conferenceDay.meeting_types.phone ||
              conferenceDay.meeting_types.virtual ||
              conferenceDay.meeting_types.in_person)
          "
          color="secondary"
          variant="outlined"
          @click="state.showMeetingTypesModal = true"
          data-cy="meeting-types-modal-btn"
        >
          edit meeting types
        </v-btn>
      </div>
    </div>

    <div class="agenda-table-con">
      <table class="agenda-table">
        <thead>
          <tr>
            <typography tag="th" color="gray600"> TIME </typography>
            <typography tag="th" color="gray600">MEETING</typography>
            <typography
              tag="th"
              color="gray600"
              v-if="showComments"
              data-cy="agenda-comment-column"
            >
              COMMENTS
            </typography>
            <typography tag="th" color="gray600" v-if="showNotAttended" data-cy="not-attended-column"
              >NOT ATTENDED</typography
            >
          </tr>
        </thead>
        <tbody>
          <template v-for="(meeting, i) in agenda || []" :key="i">
            <tr v-if="confStore.tab === 'agenda' ? meeting.flag && meeting.flag !== 'N' : true">
              <typography style="width: 70px" tag="td" color="gray800">{{
                dayjs(meeting.timeslot).format("h:mm A")
              }}</typography>
              <EmptyRow
                :conference-day="conferenceDay"
                v-if="!meeting.pta_id"
                :agenda="meeting"
                @add-break="addBreak(meeting.timeslot)"
                :loadingBreak="state.loadingBreaks.includes(meeting.timeslot)"
                @update:slot="
                  agenda![i] = $event;
                  reload();
                "
                :no-show="showNotAttended"
                :show-comments="showComments"
                :archived="archived"
              ></EmptyRow>
              <template v-else>
                <BreakRow
                  v-if="meeting.flag == 'N'"
                  :agenda="meeting"
                  :reload="reload"
                  :delete="
                    () => {
                      if (agenda)
                        agenda = agenda.map((m) => {
                          if (m.pta_id === meeting.pta_id) return { timeslot: m.timeslot } as any;
                          return m;
                        });
                    }
                  "
                  :no-show="showNotAttended"
                  :show-comments="showComments"
                  :archived="archived"
                ></BreakRow>
                <MeetingRow
                  v-if="meeting.flag == 'R'"
                  :agenda="meeting"
                  :reload="reload"
                  :delete="
                     () => {
                      if (agenda)
                        agenda = agenda.map((m) => {
                          if (m.pta_id === meeting.pta_id) return { timeslot: m.timeslot } as any;
                          return m;
                        });
                    }
                  "
                  :no-show="showNotAttended"
                  :show-comments="showComments"
                  :archived="archived"
                  :conference-day="conferenceDay"
                ></MeetingRow>
                <TeamBlockerRow
                  v-if="['O', 'T'].includes(meeting.flag)"
                  :agenda="meeting"
                  :show-comments="showComments"
                  :no-show="showNotAttended"
                  :conference-day="conferenceDay"
                ></TeamBlockerRow>
              </template>
            </tr>
          </template>
        </tbody>
      </table>
      <template v-if="!agenda?.length && loading">
        <LoadingState text="Loading Agenda"></LoadingState>
      </template>
    </div>
  </div>
  <EditMeetingType
    :conference-day="conferenceDay"
    v-model:show="state.showMeetingTypesModal"
  ></EditMeetingType>
</template>

<script setup lang="ts">
import type { GetConference, GetSchedule } from "@/api_types/teacher";
import FetchService from "@/services/fetch.service";
import { asyncComputed } from "@/utils/asyncComputed";
import dayjs from "dayjs";
import { computed, reactive } from "vue";
import Typography from "../typography.vue";
import BreakRow from "@/components/teacher/breakRow.vue";
import EmptyRow from "@/components/teacher/EmptyRow.vue";
import MeetingRow from "@/components/teacher/meetingRow.vue";
import TeamBlockerRow from "@/components/teacher/TeamBlockerRow.vue";
import { useConferenceStore } from "@/stores/teacher/conference";
import EditMeetingType from "./modal/editMeetingType.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import LoadingState from "@/components/loadingState.vue";
import { tryCatch } from "@/utils";
import { shouldShowNotAttendedColumn } from "@/components/teacher/utils";

const props = defineProps<{
  conferenceDay: GetConference["conference_days"][number];
  dayIndex: number;
}>();

const confStore = useConferenceStore();
const authStore = useAuthenticationStore();

const state = reactive({
  editVLink: false,
  linkText: (props.conferenceDay as any).virtualLink,
  showMeetingTypesModal: false,
  loadingBreaks: [] as string[]
});

const {
  data: agenda,
  reload,
  loading
} = asyncComputed(
  async (controller) => {
    const team =
      "team" in (confStore.selected || {}) ? `&team_uid=${confStore.selected?.team?.uid}` : "";
    const res = await FetchService.fetchData(
      "/schedule?conference_days_id=" + props.conferenceDay.conference_days_id + team,
      "get",
      undefined,
      undefined,
      controller
    );
    if (res)
      return (res as GetSchedule[])?.map?.((i) => ({
        ...i,
        meeting_types:
          typeof i.meeting_types == "string"
            ? tryCatch(() => JSON.parse(i.meeting_types as any), {})
            : i.meeting_types
      }));
    return [];
  },
  [() => confStore.selected?.team],
  undefined
);

const archived = computed(() => {
  const today = dayjs();
  return dayjs(props.conferenceDay.end).isBefore(today);
});

const showNotAttended = computed(() =>
  shouldShowNotAttendedColumn(!!confStore.selected?.team)
);

const showComments = computed(() => confStore.selected?.allow_comments!);

const addBreak = async (timeslot: string) => {
  state.loadingBreaks.push(timeslot);
  const res = await FetchService.fetchData("/schedule/unavailable", "post", {
    ["team" in confStore.selected! ? "team_uid" : "teacher_uid"]:
      confStore.selected?.team?.uid || authStore.me.uid,
    conference_days_id: props.conferenceDay.conference_days_id,
    timeslot
  });
  if (res?.appointment) {
    const index = agenda.value!.findIndex((s) => s.timeslot === timeslot);
    agenda.value![index] = res.appointment;
  }
  state.loadingBreaks = state.loadingBreaks.filter((s) => s !== timeslot);
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.agenda-day-con {
  margin: auto;
  width: 80%;
  background-color: $white;
  border: 1px solid $primary-75;
  border-radius: 10px;
  margin-top: 30px;
  overflow-x: auto;

  &:last-child {
    margin-bottom: 40px;
  }

  @include breakpoints.media-breakpoint-down(xlg) {
    width: 95%;
  }
  @include breakpoints.media-breakpoint-down(sm) {
    width: 98%;
  }

  .agenda-table-con {
    width: 100%;
    .agenda-table {
      width: 100%;

      border-top: 1px solid $primary-75;
      thead {
        tr {
          th {
            padding: 10px;
            text-align: center;
            border-bottom: 1px solid $primary-75;
          }
        }
      }
      tbody {
        tr {
          :deep(td) {
            padding: 4px;
            text-align: center;
          }
          
          &:not(:last-child) {
            :deep(td) {
              border-bottom: 1px solid $primary-75;
            }
          }
        }
      }
    }
  }

  .day-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
    width: 100%;
    flex-wrap: wrap;
    grid-gap: 10px;

    .day-index {
      margin-right: 15px;
    }
  }
}

.edit-virtual-input {
  width: 300px;
  max-width: 100%;

  :deep(.v-field) {
    padding-right: 0;
  }

  :deep(.v-field__append-inner) {
    padding-top: 0;
  }

  :deep(.v-field__append-inner button) {
    height: 100%;
    border-width: 2px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    z-index: 1;
  }

  :deep(.v-field__prepend-inner) {
    padding-top: 0;
    align-items: center;
  }
}
</style>
